/**
 * Project Details — inline-editable attribute card.
 *
 * Mirrors the "Initiative Details" card on InitiativeDetail.tsx. The
 * design intent (May 2026): inline editing on entity detail pages is
 * the universal pattern, modals are creation-only. This card brings
 * the Project Detail page in line with that rule — every attribute
 * the (legacy) EditProjectDialog modal touches is editable here
 * without leaving the page.
 *
 * Save semantics: each editor commits on blur (and Enter for text /
 * number inputs). Mutation goes through useUpdateProject — same
 * server path the modal used, so status pulse / baseline / drift
 * side-effects still fire correctly.
 *
 * The card intentionally does NOT include role allocations (those
 * live on the Capacity tab) or notes / details / status_update
 * (those have dedicated rich-text editors elsewhere on the page).
 */
import { useMemo, useState } from "react";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { fmtMoneyExact, readBlendedRate } from "@/lib/cost";
import { EditableField } from "@/components/ui/EditableField";
import { SponsorPicker } from "@/components/ui/SponsorPicker";
import {
  STATUS_OPTIONS,
  HEALTH_OPTIONS,
  statusLabel,
  statusPillClass,
  effectiveHealth,
} from "@/lib/projectStatus";
import { usePortfolio, useUpdateProject } from "@/hooks/usePortfolio";
import { costAvoidance, inYearBenefitForRow, inYearOverride } from "@/lib/benefit";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useRoster } from "@/hooks/useRoster";
import type { Project } from "@/types/project";

const YEAR = new Date().getFullYear();

const inputCls =
  "rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300";

const PRIORITY_OPTIONS = ["Highest", "High", "Medium", "Low"];

const PHASE_OPTIONS = [
  { value: "", label: "— not set —" },
  { value: "planning", label: "Planning" },
  { value: "execution", label: "Execution" },
  { value: "testing_go_live", label: "Testing / Go Live" },
];

const TSHIRT_OPTIONS = [
  "S: < 80 Hours",
  "M: 80-160 Hours",
  "L: 160-320 Hours",
  "XL: 320-640 Hours",
  "XXL: > 640 Hours",
];

export function ProjectDetailsCard({ project }: { project: Project }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [addingDept, setAddingDept] = useState(false);
  const updateProject = useUpdateProject();
  const portfolio = usePortfolio(false);
  const { data: roster } = useRoster();
  // Blended rate — for the DERIVED Synnergie cost (never stored).
  const { data: costSettings } = useAppSettings("cost");
  const blendedRate = readBlendedRate(costSettings);

  // Department options — every department string in use across projects.
  const departmentOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of portfolio.data ?? []) {
      const v = (p.department ?? "").trim();
      if (v) set.add(v);
    }
    return Array.from(set).sort();
  }, [portfolio.data]);

  const teamOptions = useMemo(() => {
    const set = new Set<string>();
    for (const m of roster ?? []) {
      if (m.team) set.add(m.team);
    }
    return Array.from(set).sort();
  }, [roster]);

  const pmOptions = useMemo(
    () => (roster ?? []).filter((m: any) => m.role_key === "pm"),
    [roster],
  );

  const saveField = (field: string, value: unknown) => saveFields({ [field]: value });
  const saveFields = (fields: Record<string, unknown>) => {
    updateProject.mutate(
      { id: project.id, ...fields } as any,
      {
        onSuccess: () => {
          setEditing(null);
          setAddingDept(false);
          toast.success("Updated");
        },
        // Show the backend's validation message ("end_date cannot be
        // before start_date"), not axios's generic "status code 400" —
        // a rejected save otherwise looks like the field reverting on
        // its own (Brett, 2026-08-28).
        onError: (err: any) =>
          toast.error(err?.response?.data?.detail ?? (err as Error).message),
      },
    );
  };

  // Common closers for editor onBlur — only collapse if we're not in
  // the special "adding department" mode (which has its own blur
  // handling).
  const closeEditor = () => setEditing(null);

  // Go-live change note (Jim K's board-email review, 2026-09-21): when
  // the end date moves, ask why and who approved — the Weekly Update
  // prints it. Both optional; Skip saves the date alone.
  const [dateChange, setDateChange] = useState<{ next: string | null } | null>(null);
  const [changeReason, setChangeReason] = useState("");
  const [changeApprover, setChangeApprover] = useState("");
  const commitDateChange = (withNote: boolean) => {
    if (!dateChange) return;
    const payload: Record<string, unknown> = { end_date: dateChange.next };
    if (withNote) {
      if (changeReason.trim()) payload.change_reason = changeReason.trim();
      if (changeApprover.trim()) payload.change_approved_by = changeApprover.trim();
    }
    updateProject.mutate(
      { id: project.id, ...payload } as any,
      {
        onSuccess: () => {
          setEditing(null);
          setDateChange(null);
          setChangeReason("");
          setChangeApprover("");
          toast.success(withNote && (changeReason.trim() || changeApprover.trim()) ? "Go-live updated — change noted for the weekly update" : "Go-live updated");
        },
        onError: (err: any) => toast.error(err?.response?.data?.detail ?? (err as Error).message),
      },
    );
  };

  // ----- field renderers -----

  const dateDisplay = (iso: string | null | undefined) =>
    iso ? new Date(iso + "T00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <div className="flex items-center gap-2 mb-5">
        <Settings2 className="h-4 w-4 text-brand-navy-500" />
        <h2 className="text-sm font-semibold text-ink-2">Project Details</h2>
        <span className="ml-2 text-[10px] text-ink-subtle">click any field to edit</span>
      </div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-xs md:grid-cols-3">
        {/* ID — read-only */}
        <EditableField label="ID" value={project.id} />

        {/* Name */}
        <EditableField
          label="Name"
          editing={editing === "name"}
          onEdit={() => setEditing("name")}
          display={<span className="text-ink">{project.name}</span>}
          editor={
            <input
              autoFocus
              type="text"
              defaultValue={project.name}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveField("name", (e.target as HTMLInputElement).value.trim());
                if (e.key === "Escape") closeEditor();
              }}
              onBlur={(e) => saveField("name", e.target.value.trim())}
              className={inputCls}
            />
          }
        />

        {/* Status */}
        <EditableField
          label="Status"
          editing={editing === "status"}
          onEdit={() => setEditing("status")}
          display={
            <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", statusPillClass(project.status))}>
              {statusLabel(project.status)}
            </span>
          }
          editor={
            <select autoFocus defaultValue={project.status ?? ""}
              onChange={(e) => saveField("status", e.target.value || null)}
              onBlur={closeEditor}
              className={inputCls}>
              <option value="">—</option>
              {STATUS_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          }
        />

        {/* Health */}
        <EditableField
          label="Health"
          editing={editing === "health"}
          onEdit={() => setEditing("health")}
          display={(() => {
            const eh = effectiveHealth(project.status, project.health);
            return (
              <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", eh.pillClass)}>
                {eh.label}
              </span>
            );
          })()}
          editor={
            <select autoFocus defaultValue={(project.health ?? "").toLowerCase()}
              onChange={(e) => saveField("health", e.target.value || null)}
              onBlur={closeEditor}
              className={inputCls}>
              <option value="">—</option>
              {HEALTH_OPTIONS.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
            </select>
          }
        />

        {/* Priority */}
        <EditableField
          label="Priority"
          editing={editing === "priority"}
          onEdit={() => setEditing("priority")}
          display={<span className="text-ink">{project.priority ?? "—"}</span>}
          editor={
            <select autoFocus defaultValue={project.priority ?? ""}
              onChange={(e) => saveField("priority", e.target.value || null)}
              onBlur={closeEditor}
              className={inputCls}>
              <option value="">—</option>
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          }
        />

        {/* Department — same select+add-new pattern as the modal */}
        <EditableField
          label="Department"
          editing={editing === "department"}
          onEdit={() => { setEditing("department"); setAddingDept(false); }}
          display={<span className="text-ink">{project.department ?? "—"}</span>}
          editor={
            addingDept ? (
              <input
                autoFocus
                type="text"
                placeholder="New department name"
                defaultValue=""
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = (e.target as HTMLInputElement).value.trim();
                    saveField("department", v || null);
                  } else if (e.key === "Escape") {
                    setAddingDept(false);
                    closeEditor();
                  }
                }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  saveField("department", v || null);
                }}
                className={inputCls}
              />
            ) : (
              <select autoFocus defaultValue={project.department ?? ""}
                onChange={(e) => {
                  if (e.target.value === "__add_new__") {
                    setAddingDept(true);
                  } else {
                    saveField("department", e.target.value || null);
                  }
                }}
                onBlur={() => { if (!addingDept) closeEditor(); }}
                className={inputCls}>
                <option value="">— none —</option>
                {departmentOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                {project.department && !departmentOptions.includes(project.department) && (
                  <option value={project.department}>{project.department}</option>
                )}
                <option value="__add_new__">+ Add new…</option>
              </select>
            )
          }
        />

        {/* Sponsor — same select+add-new pattern as Department.
            Options sourced from the sponsors already in use across
            projects. */}
        <EditableField
          label="Sponsor"
          editing={editing === "sponsor"}
          onEdit={() => setEditing("sponsor")}
          display={<span className="text-ink">{project.sponsor ?? "—"}</span>}
          editor={
            <SponsorPicker
              value={project.sponsor}
              onChange={(v) => saveField("sponsor", v)}
              onCancel={closeEditor}
            />
          }
        />

        {/* IT Involved — mirrors the initiative field. 'Y' surfaces
            on the roadmap so the IT footprint of supporting projects
            is visible at a glance. */}
        <EditableField
          label="IT Involved"
          editing={editing === "it_involved"}
          onEdit={() => setEditing("it_involved")}
          display={
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                project.it_involved === "Y"
                  ? "bg-brand-navy-100 dark:bg-brand-navy-500/15 text-brand-navy-700 dark:text-brand-navy-300"
                  : project.it_involved === "N"
                    ? "bg-raised text-ink-muted"
                    : "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
              )}
            >
              {project.it_involved === "Y"
                ? "Yes"
                : project.it_involved === "N"
                  ? "No"
                  : "TBD"}
            </span>
          }
          editor={
            <select autoFocus defaultValue={project.it_involved ?? "Unknown"}
              onChange={(e) => saveField("it_involved", e.target.value)}
              onBlur={closeEditor}
              className={inputCls}>
              <option value="Y">Yes</option>
              <option value="N">No</option>
              <option value="Unknown">TBD</option>
            </select>
          }
        />

        {/* Team */}
        <EditableField
          label="Team"
          editing={editing === "team"}
          onEdit={() => setEditing("team")}
          display={<span className="text-ink">{(project as any).team ?? "—"}</span>}
          editor={
            <select autoFocus defaultValue={(project as any).team ?? ""}
              onChange={(e) => saveField("team", e.target.value || null)}
              onBlur={closeEditor}
              className={inputCls}>
              <option value="">—</option>
              {teamOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          }
        />

        {/* PM */}
        <EditableField
          label="PM"
          editing={editing === "pm"}
          onEdit={() => setEditing("pm")}
          display={<span className="text-ink">{(project as any).pm ?? "—"}</span>}
          editor={
            <select autoFocus defaultValue={(project as any).pm ?? ""}
              onChange={(e) => saveField("pm", e.target.value || null)}
              onBlur={closeEditor}
              className={inputCls}>
              <option value="">—</option>
              {pmOptions.map((m: any) => <option key={m.name} value={m.name}>{m.name}</option>)}
            </select>
          }
        />

        {/* Current Phase */}
        <EditableField
          label="Current Phase"
          editing={editing === "current_phase"}
          onEdit={() => setEditing("current_phase")}
          display={(() => {
            // Render the selector's LABEL, not the stored slug. Without
            // this, picking "Execution" in the dropdown showed up as
            // the raw lowercase "execution" on the page.
            const opt = PHASE_OPTIONS.find((o) => o.value === (project.current_phase ?? ""));
            return <span className="text-ink">{opt && opt.value ? opt.label : "—"}</span>;
          })()}
          editor={
            <select autoFocus defaultValue={project.current_phase ?? ""}
              onChange={(e) => saveField("current_phase", e.target.value || null)}
              onBlur={closeEditor}
              className={inputCls}>
              {PHASE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          }
        />

        {/* T-Shirt Size */}
        <EditableField
          label="T-Shirt Size"
          editing={editing === "tshirt_size"}
          onEdit={() => setEditing("tshirt_size")}
          display={<span className="text-ink">{project.tshirt_size ?? "—"}</span>}
          editor={
            <select autoFocus defaultValue={project.tshirt_size ?? ""}
              onChange={(e) => saveField("tshirt_size", e.target.value || null)}
              onBlur={closeEditor}
              className={inputCls}>
              <option value="">—</option>
              {TSHIRT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          }
        />

        {/* Est. Hours */}
        <EditableField
          label="Est. Hours"
          editing={editing === "est_hours"}
          onEdit={() => setEditing("est_hours")}
          display={<span className="text-ink tabular-nums">{project.est_hours ?? 0}</span>}
          editor={
            <input autoFocus type="number" min={0} step={1}
              defaultValue={project.est_hours ?? 0}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveField("est_hours", parseFloat((e.target as HTMLInputElement).value) || 0);
                if (e.key === "Escape") closeEditor();
              }}
              onBlur={(e) => saveField("est_hours", parseFloat(e.target.value) || 0)}
              className={cn(inputCls, "w-24")}
            />
          }
        />

        {/* Synnergie hours — manual vendor hours. The cost next to it is
            derived (hours x blended rate) and deliberately labeled
            EXTERNAL: this is extra out-of-pocket spend, not the sunk
            cost of people already on payroll (Brett, 2026-08-28). */}
        <EditableField
          label="Synnergie Hours"
          editing={editing === "synnergie_hours"}
          onEdit={() => setEditing("synnergie_hours")}
          display={
            <span className="tabular-nums">
              {project.synnergie_hours != null ? (
                <>
                  <span className="text-ink">{project.synnergie_hours}</span>
                  <span
                    className="ml-2 rounded-full bg-violet-50 dark:bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-500/30"
                    title={`${project.synnergie_hours} h × $${blendedRate}/h — Synnergie is EXTRA external spend, on top of internal capacity`}
                  >
                    {fmtMoneyExact(project.synnergie_hours * blendedRate)} external
                  </span>
                </>
              ) : (
                <span className="text-ink-faint">—</span>
              )}
            </span>
          }
          editor={
            <input autoFocus type="number" min={0} step={1}
              defaultValue={project.synnergie_hours ?? ""}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = (e.target as HTMLInputElement).value.trim();
                  saveField("synnergie_hours", v === "" ? null : parseFloat(v) || 0);
                }
                if (e.key === "Escape") closeEditor();
              }}
              onBlur={(e) => {
                const v = e.target.value.trim();
                saveField("synnergie_hours", v === "" ? null : parseFloat(v) || 0);
              }}
              className={cn(inputCls, "w-24")}
            />
          }
        />

        {/* Annual benefit — the business's annualized EBITDA estimate
            (dollars/year, from the prioritization deck). Shown on Top 10
            rows on /top-projects; the case for the project's rank. */}
        <EditableField
          label="Annual EBITDA"
          editing={editing === "annual_benefit"}
          onEdit={() => setEditing("annual_benefit")}
          display={
            <span className="tabular-nums">
              {project.annual_benefit != null ? (
                <span className="text-emerald-700 dark:text-emerald-300 font-medium">
                  {fmtMoneyExact(project.annual_benefit)}
                  {project.benefit_one_time ? " one-time" : "/yr"}
                </span>
              ) : (
                <span className="text-ink-faint">—</span>
              )}
            </span>
          }
          editor={
            <input autoFocus type="number" min={0} step={100}
              defaultValue={project.annual_benefit ?? ""}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = (e.target as HTMLInputElement).value.trim();
                  saveField("annual_benefit", v === "" ? null : parseFloat(v) || 0);
                }
                if (e.key === "Escape") closeEditor();
              }}
              onBlur={(e) => {
                const v = e.target.value.trim();
                saveField("annual_benefit", v === "" ? null : parseFloat(v) || 0);
              }}
              className={cn(inputCls, "w-32")}
            />
          }
        />

        {/* Benefit type — per year (annualized, prorated by go-live on
            the board) or ONE-TIME (a single stocking order: counted in
            full in its go-live year, never annualized). Brett, 2026-09-16
            — ETE-318 NAPA EDI. */}
        <EditableField
          label="Benefit Type"
          editing={editing === "benefit_one_time"}
          onEdit={() => setEditing("benefit_one_time")}
          display={
            <span className="text-ink-2" data-testid="benefit-type">
              {project.benefit_one_time ? "One-time" : "Per year"}
            </span>
          }
          editor={
            <select autoFocus defaultValue={project.benefit_one_time ? "one_time" : "annual"}
              onChange={(e) => saveField("benefit_one_time", e.target.value === "one_time")}
              onBlur={closeEditor}
              className={inputCls}
              aria-label="Benefit type">
              <option value="annual">Per year (annualized)</option>
              <option value="one_time">One-time (not annualized)</option>
            </select>
          }
        />

        {/* In-year benefit — what THIS year actually captures. Normally
            prorated from go-live (see lib/benefit.ts); an entered figure
            replaces that for this year only. Brett, 2026-09-22 — ETE-318
            NAPA EDI is $304,200/yr but only one quarterly hit lands in 2026. */}
        <EditableField
          label={`${YEAR} EBITDA`}
          editing={editing === "in_year_benefit_override"}
          onEdit={() => setEditing("in_year_benefit_override")}
          display={
            <span className="tabular-nums" data-testid="in-year-override">
              {inYearOverride(project, YEAR) != null ? (
                <>
                  <span className="text-emerald-700 dark:text-emerald-300 font-medium">{fmtMoneyExact(inYearOverride(project, YEAR)!)}</span>
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide text-ink-subtle">entered</span>
                </>
              ) : inYearBenefitForRow(project, YEAR) != null ? (
                <>
                  <span className="text-ink-2">{fmtMoneyExact(inYearBenefitForRow(project, YEAR)!)}</span>
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide text-ink-subtle">prorated from go-live</span>
                </>
              ) : (
                <span className="text-ink-faint">—</span>
              )}
            </span>
          }
          editor={
            <input autoFocus type="number" min={0} step={100}
              defaultValue={inYearOverride(project, YEAR) ?? ""}
              placeholder="Blank = prorate from go-live"
              aria-label={`${YEAR} benefit`}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = (e.target as HTMLInputElement).value.trim();
                  saveFields(v === ""
                    ? { in_year_benefit_override: null, in_year_benefit_override_year: null }
                    : { in_year_benefit_override: parseFloat(v) || 0, in_year_benefit_override_year: YEAR });
                }
                if (e.key === "Escape") closeEditor();
              }}
              onBlur={(e) => {
                const v = e.target.value.trim();
                saveFields(v === ""
                  ? { in_year_benefit_override: null, in_year_benefit_override_year: null }
                  : { in_year_benefit_override: parseFloat(v) || 0, in_year_benefit_override_year: YEAR });
              }}
              className={cn(inputCls, "w-40")}
            />
          }
        />

        {/* Cost avoidance (Brett, 2026-10-16) — projects with no EBITDA
            that give someone time back. Hours saved per month × 12 × the
            blended rate (Settings → Cost, default $65/hr); an entered
            annual figure replaces the arithmetic. Shown on /top-projects
            between Go-live and Annual EBITDA. */}
        <EditableField
          label="Cost Avoidance"
          editing={editing === "cost_avoidance"}
          onEdit={() => setEditing("cost_avoidance")}
          display={
            <span className="tabular-nums" data-testid="cost-avoidance-value">
              {costAvoidance(project, blendedRate) != null ? (
                <>
                  <span className="text-sky-700 dark:text-sky-300 font-medium">{fmtMoneyExact(costAvoidance(project, blendedRate)!)}/yr</span>
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide text-ink-subtle">
                    {project.cost_avoidance_annual != null
                      ? "entered"
                      : `${project.cost_avoidance_hours_per_month} h/mo × 12 × ${fmtMoneyExact(blendedRate)}/hr`}
                  </span>
                </>
              ) : (
                <span className="text-ink-faint">—</span>
              )}
            </span>
          }
          editor={
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <label className="flex items-center gap-1">
                <span className="text-ink-muted">Hours saved / month</span>
                <input autoFocus type="number" min={0} step={1}
                  defaultValue={project.cost_avoidance_hours_per_month ?? ""}
                  aria-label="Hours saved per month"
                  id="cost_avoidance_hours"
                  className={cn(inputCls, "w-20")}
                />
              </label>
              <label className="flex items-center gap-1">
                <span className="text-ink-muted">or $/yr</span>
                <input type="number" min={0} step={100}
                  defaultValue={project.cost_avoidance_annual ?? ""}
                  placeholder="Blank = calculate"
                  aria-label="Cost avoidance per year"
                  id="cost_avoidance_annual"
                  className={cn(inputCls, "w-28")}
                />
              </label>
              <button
                type="button"
                className="rounded-md bg-brand-navy-600 px-2 py-1 text-xs font-semibold text-white"
                onClick={() => {
                  const h = (document.getElementById("cost_avoidance_hours") as HTMLInputElement).value.trim();
                  const d = (document.getElementById("cost_avoidance_annual") as HTMLInputElement).value.trim();
                  saveFields({
                    cost_avoidance_hours_per_month: h === "" ? null : parseFloat(h) || 0,
                    cost_avoidance_annual: d === "" ? null : parseFloat(d) || 0,
                  });
                }}
              >
                Save
              </button>
              <button type="button" className="text-ink-muted" onClick={closeEditor}>Cancel</button>
              <span className="w-full text-[11px] text-ink-subtle">Priced at the blended rate {fmtMoneyExact(blendedRate)}/hr (Settings → Cost). A $/yr figure wins over the hours.</span>
            </div>
          }
        />

        {/* Progress */}
        <EditableField
          label="Progress"
          editing={editing === "pct_complete"}
          onEdit={() => setEditing("pct_complete")}
          display={
            <span className="text-ink tabular-nums">
              {Math.round((project.pct_complete ?? 0) * 100)}%
            </span>
          }
          editor={
            <input autoFocus type="number" min={0} max={100} step={1}
              defaultValue={Math.round((project.pct_complete ?? 0) * 100)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = Math.max(0, Math.min(100, parseFloat((e.target as HTMLInputElement).value) || 0));
                  saveField("pct_complete", v / 100);
                }
                if (e.key === "Escape") closeEditor();
              }}
              onBlur={(e) => {
                const v = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                saveField("pct_complete", v / 100);
              }}
              className={cn(inputCls, "w-20")}
            />
          }
        />

        {/* Start Date */}
        <EditableField
          label="Start Date"
          editing={editing === "start_date"}
          onEdit={() => setEditing("start_date")}
          display={<span className="text-ink tabular-nums">{dateDisplay(project.start_date)}</span>}
          editor={
            <input autoFocus type="date"
              defaultValue={project.start_date ?? ""}
              onBlur={(e) => saveField("start_date", e.target.value || null)}
              className={inputCls}
            />
          }
        />

        {/* End Date */}
        <EditableField
          label="End Date"
          editing={editing === "end_date"}
          onEdit={() => setEditing("end_date")}
          display={<span className="text-ink tabular-nums">{dateDisplay(project.end_date)}</span>}
          editor={
            <input autoFocus type="date"
              defaultValue={project.end_date ?? ""}
              onBlur={(e) => {
                const next = e.target.value || null;
                if (next === (project.end_date ?? null)) { closeEditor(); return; }
                setDateChange({ next });
              }}
              className={inputCls}
            />
          }
        />
        {dateChange && (
          <div className="col-span-full rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/10 p-3" data-testid="date-change-note">
            <div className="text-xs font-semibold text-amber-900 dark:text-amber-300">
              Go-live moving {dateDisplay(project.end_date)} → {dateDisplay(dateChange.next)}. Why, and who approved?
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <input
                autoFocus
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="Reason (one line)"
                aria-label="Reason for the go-live change"
                className={cn(inputCls, "w-full")}
              />
              <input
                value={changeApprover}
                onChange={(e) => setChangeApprover(e.target.value)}
                placeholder="Approved by"
                aria-label="Who approved the go-live change"
                className={cn(inputCls, "w-full")}
                onKeyDown={(e) => { if (e.key === "Enter") commitDateChange(true); }}
              />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button type="button" onClick={() => commitDateChange(true)} className="rounded-md bg-brand-navy-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-navy-700">
                Save
              </button>
              <button type="button" onClick={() => commitDateChange(false)} className="rounded-md border border-line bg-surface px-3 py-1 text-xs text-ink-3 hover:bg-raised">
                Skip note
              </button>
              <button type="button" onClick={() => { setDateChange(null); setEditing(null); }} className="ml-auto text-xs text-ink-muted hover:underline">
                Cancel
              </button>
              <span className="text-[11px] text-ink-muted">Shows in the Monday board email.</span>
            </div>
          </div>
        )}

        {/* Spec dates — grouped at the end since they're lifecycle-specific */}
        <EditableField
          label="Func Spec Due"
          editing={editing === "functional_spec_due"}
          onEdit={() => setEditing("functional_spec_due")}
          display={<span className="text-ink tabular-nums">{dateDisplay(project.functional_spec_due)}</span>}
          editor={
            <input autoFocus type="date"
              defaultValue={project.functional_spec_due ?? ""}
              onBlur={(e) => saveField("functional_spec_due", e.target.value || null)}
              className={inputCls}
            />
          }
        />

        <EditableField
          label="Func Spec Done"
          editing={editing === "functional_spec_completed"}
          onEdit={() => setEditing("functional_spec_completed")}
          display={<span className="text-ink tabular-nums">{dateDisplay(project.functional_spec_completed)}</span>}
          editor={
            <input autoFocus type="date"
              defaultValue={project.functional_spec_completed ?? ""}
              onBlur={(e) => saveField("functional_spec_completed", e.target.value || null)}
              className={inputCls}
            />
          }
        />

        <EditableField
          label="Tech Spec Due"
          editing={editing === "technical_spec_due"}
          onEdit={() => setEditing("technical_spec_due")}
          display={<span className="text-ink tabular-nums">{dateDisplay(project.technical_spec_due)}</span>}
          editor={
            <input autoFocus type="date"
              defaultValue={project.technical_spec_due ?? ""}
              onBlur={(e) => saveField("technical_spec_due", e.target.value || null)}
              className={inputCls}
            />
          }
        />

        <EditableField
          label="Tech Spec Done"
          editing={editing === "technical_spec_completed"}
          onEdit={() => setEditing("technical_spec_completed")}
          display={<span className="text-ink tabular-nums">{dateDisplay(project.technical_spec_completed)}</span>}
          editor={
            <input autoFocus type="date"
              defaultValue={project.technical_spec_completed ?? ""}
              onBlur={(e) => saveField("technical_spec_completed", e.target.value || null)}
              className={inputCls}
            />
          }
        />
      </div>

      {/* Submitter — surfaced only when the project actually came from
          the /request wizard. Read-only: this captures point-in-time
          intake identity, not a maintained relationship. mailto: gives
          PMO one click to follow up at triage. */}
      {(project.requested_by_name || project.requested_by_email) && (
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line-soft pt-3 text-xs">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
            Submitted by
          </span>
          <span className="text-ink">{project.requested_by_name || "—"}</span>
          {project.requested_by_email && (
            <a
              href={`mailto:${project.requested_by_email}?subject=${encodeURIComponent(`Re: ${project.id} — ${project.name}`)}`}
              className="font-mono text-[11px] text-brand-navy-700 dark:text-brand-navy-300 hover:underline"
            >
              {project.requested_by_email}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
